import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Eye, Edit, Ban, Check, X, RefreshCw, UserPlus, Trash2 } from 'lucide-react';
import UserDetailModal from './UserDetailModal';
import CreateUserModal from './CreateUserModal';
import { useToast } from '../../hooks/use-toast';

const UsersTab = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [search, planFilter, statusFilter, page]);

  useEffect(() => {
    // Update selectAll checkbox state based on current page selections
    if (users.length > 0) {
      const allCurrentPageSelected = users.every(user => selectedUserIds.includes(user.id));
      setSelectAll(allCurrentPageSelected);
    }
  }, [users, selectedUserIds]);

  useEffect(() => {
    // Reset selections when filters change (but NOT when page changes)
    setSelectedUserIds([]);
    setSelectAll(false);
  }, [search, planFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      console.log('🔑 Admin token:', token ? 'Present' : 'Missing');
      console.log('🌐 API URL:', API_BASE_URL);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
        ...(planFilter && { plan: planFilter }),
        ...(statusFilter && { status: statusFilter })
      });

      const url = `${API_BASE_URL}/api/admin/users?${params}`;
      console.log('📡 Fetching:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('📥 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Users data:', data);
        setUsers(data.users);
        setTotalPages(data.total_pages);
        setTotalUsers(data.total || 0);
      } else {
        const error = await response.text();
        console.error('❌ Error response:', error);
      }
    } catch (error) {
      console.error('❌ Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all on current page
      const currentPageIds = users.map(user => user.id);
      setSelectedUserIds(selectedUserIds.filter(id => !currentPageIds.includes(id)));
      setSelectAll(false);
    } else {
      // Select all on current page (add to existing selections)
      const currentPageIds = users.map(user => user.id);
      const newSelections = [...new Set([...selectedUserIds, ...currentPageIds])];
      setSelectedUserIds(newSelections);
      setSelectAll(true);
    }
  };

  const handleSelectAllUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: '1',
        limit: totalUsers.toString(),
        ...(search && { search }),
        ...(planFilter && { plan: planFilter }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const allUserIds = data.users.map(user => user.id);
        setSelectedUserIds(allUserIds);
        toast({
          title: "✅ All Users Selected",
          description: `Successfully selected all ${allUserIds.length} users matching your filters.`,
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error selecting all users:', error);
      toast({
        title: "Selection Failed",
        description: "Failed to select all users. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedUserIds, userId];
      setSelectedUserIds(newSelected);
      if (newSelected.length === users.length) {
        setSelectAll(true);
      }
    }
  };

  const handleExportCSV = async () => {
    console.log('🚀 Export Contacts clicked');
    
    if (selectedUserIds.length === 0) {
      toast({
        title: "No Users Selected",
        description: "Please select at least one user to export.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      console.log('🔑 Admin token:', token ? 'Present' : 'Missing');
      console.log('📋 Selected users:', selectedUserIds.length);
      
      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please login again to continue.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users/export/csv`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: selectedUserIds })
      });

      console.log('📥 Export response:', response.status, response.statusText);

      if (response.ok) {
        const blob = await response.blob();
        console.log('📦 Blob size:', blob.size, 'bytes, type:', blob.type);
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `contacts_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadUrl);
          document.body.removeChild(a);
        }, 100);
        
        console.log('✅ CSV export successful');
        toast({
          title: "✅ Export Successful!",
          description: `Successfully exported ${selectedUserIds.length} contacts to CSV.`,
          duration: 3000,
        });
        setSelectedUserIds([]);
        setSelectAll(false);
      } else {
        const errorText = await response.text();
        console.error('❌ Export failed:', response.status, errorText);
        toast({
          title: "Export Failed",
          description: `Failed to export contacts: ${errorText.substring(0, 100)}`,
          variant: "destructive",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('❌ Error exporting CSV:', error);
      toast({
        title: "Export Error",
        description: `Error exporting CSV: ${error.message}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleBulkDelete = async () => {
    console.log('🗑️ Bulk Delete clicked');
    console.log('📋 Selected user IDs:', selectedUserIds);
    
    if (selectedUserIds.length === 0) {
      toast({
        title: "No Users Selected",
        description: "Please select at least one user to delete.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Confirm deletion
    const confirmMessage = `Are you sure you want to delete ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please login again to continue.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: selectedUserIds })
      });

      console.log('📥 Delete response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Bulk delete successful:', data);
        
        toast({
          title: "✅ Users Deleted!",
          description: `Successfully deleted ${data.deleted_count} user${data.deleted_count > 1 ? 's' : ''}.`,
          duration: 3000,
        });
        
        // Clear selections and refresh
        setSelectedUserIds([]);
        setSelectAll(false);
        fetchUsers();
      } else {
        const errorData = await response.json();
        console.error('❌ Delete failed:', response.status, errorData);
        toast({
          title: "Delete Failed",
          description: errorData.detail || "Failed to delete users",
          variant: "destructive",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('❌ Error deleting users:', error);
      toast({
        title: "Delete Error",
        description: `Error deleting users: ${error.message}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const getPlanBadge = (user) => {
    if (user.stripe_subscription_id && user.subscription_plan === 'growth' && user.subscription_status === 'active') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-400 to-emerald-500 text-white">Paid</span>;
    } else if (user.subscription_plan === 'growth_trial' || user.subscription_status === 'trial' || user.subscription_status === 'growth_trial') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-400 to-indigo-500 text-white">Trial</span>;
    } else {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">Free</span>;
    }
  };

  const getStatusBadge = (user) => {
    if (user.is_banned) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Banned</span>;
    } else if (user.is_active === false) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Inactive</span>;
    } else {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>;
    }
  };

  return (
    <div>
      {/* Header with Create Button */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">App Users</h2>
          <p className="text-gray-600">Manage your application users</p>
        </div>
        <button
          onClick={() => setShowCreateUserModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
        >
          <UserPlus className="w-5 h-5" />
          Create User
        </button>
      </div>

      {/* Filters & Search */}
      <div className="mb-6 relative overflow-hidden rounded-2xl p-6 backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-300"
            />
          </div>

          {/* Plan Filter */}
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-300"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="trial">Trial</option>
            <option value="paid">Paid</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-300"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="banned">Banned</option>
          </select>

          {/* Selection Actions */}
          <button
            onClick={handleSelectAllUsers}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-400 to-indigo-500 text-white font-medium hover:shadow-lg transition-all"
          >
            <Check className="w-4 h-4" />
            Select All Users ({totalUsers})
          </button>
          
          {/* Export Button */}
          <button
            onClick={handleExportCSV}
            disabled={selectedUserIds.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-green-400 to-emerald-500 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export Contacts {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
          </button>
          
          {/* Bulk Delete Button */}
          <button
            onClick={handleBulkDelete}
            disabled={selectedUserIds.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            Delete Users {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-rose-400 animate-spin mb-4" />
            <p className="text-gray-600">Loading users...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-rose-50 to-pink-50">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-rose-500 rounded focus:ring-rose-400 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-rose-50/50 transition-colors">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                        className="w-4 h-4 text-rose-500 rounded focus:ring-rose-400 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.email || 'No email'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPlanBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDetailModal(true);
                        }}
                        className="p-2 hover:bg-rose-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-rose-500" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {showDetailModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedUser(null);
            fetchUsers(); // Refresh list
          }}
        />
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <CreateUserModal
          onClose={() => setShowCreateUserModal(false)}
          onUserCreated={() => {
            fetchUsers(); // Refresh list
          }}
        />
      )}
    </div>
  );
};

export default UsersTab;